import React, { useEffect, useState } from 'react'
import {
  Alert, Button, CircularProgress, MenuItem, Paper, Select, Snackbar, TextField, Typography
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import PageHeader from '../PageHeader'
import { groupCreationService } from '../../services/groupCreationService'
import ReactGA from 'react-ga4'

// Permission domains the backend recognises, with friendly labels.
const PERMISSION_DOMAINS = [
  { key: 'userData', label: 'User Data' },
  { key: 'notif', label: 'Notifications' },
  { key: 'ad', label: 'Advertisements' },
  { key: 'challenge', label: 'Challenges' },
  { key: 'group', label: 'Group Management' }
]

// Levels in ascending order. '' means no access for that domain.
const PERMISSION_LEVELS = ['viewer', 'editor', 'publisher']

const initialLevels = PERMISSION_DOMAINS.reduce((acc, d) => ({ ...acc, [d.key]: '' }), {})

// Turn the per-domain chosen level into the flag object the membership stores,
// setting every level up to and including the chosen one (e.g. editor implies
// viewer). This matches how the backend route handlers OR the levels together.
const buildPermissions = (levels) => {
  const permissions = {}
  for (const domain of PERMISSION_DOMAINS) {
    const chosen = levels[domain.key]
    const cutoff = PERMISSION_LEVELS.indexOf(chosen)
    if (cutoff === -1) continue
    for (let i = 0; i <= cutoff; i++) {
      permissions[`${domain.key}_${PERMISSION_LEVELS[i]}`] = true
    }
  }
  return permissions
}

const GroupCreate = () => {
  const { groupId } = useParams()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [joinCode, setJoinCode] = useState('')

  const [adminEmail, setAdminEmail] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState(null)

  const [levels, setLevels] = useState(initialLevels)

  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' })

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window?.location?.pathname ?? '',
      title: 'Group Create Page'
    })
  }, [])

  // Search users by email (debounced) whenever the query changes and no admin
  // is already selected.
  useEffect(() => {
    if (selectedAdmin || adminEmail.length < 3) {
      setResults([])
      return
    }
    const controller = new AbortController()
    setSearching(true)
    const timeout = setTimeout(() => {
      groupCreationService.searchUsers(groupId, adminEmail, controller.signal)
        .then(users => setResults(users || []))
        .catch(() => {})
        .finally(() => setSearching(false))
    }, 350)

    return () => {
      controller.abort()
      clearTimeout(timeout)
    }
  }, [groupId, adminEmail, selectedAdmin])

  const setLevel = (domainKey, level) => {
    setLevels(prev => ({ ...prev, [domainKey]: level }))
  }

  const clearAdmin = () => {
    setSelectedAdmin(null)
    setAdminEmail('')
    setResults([])
  }

  const isValid = name.trim() && joinCode.trim().length >= 5 && selectedAdmin

  const submit = async () => {
    if (!isValid) return
    setSubmitting(true)
    try {
      const res = await groupCreationService.createGroup(groupId, {
        name: name.trim(),
        joinCode: joinCode.trim(),
        adminUserId: selectedAdmin.id,
        permissions: buildPermissions(levels)
      })
      setToast({ open: true, severity: 'success', message: res.message || 'Group created' })
      setTimeout(() => navigate(`/dashboard/${groupId}/home`), 1200)
    } catch (err) {
      const message = err?.response?.data?.message || 'There was an error creating the group'
      setToast({ open: true, severity: 'error', message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageHeader title='Create a Group' />
      <Paper className='p-6 max-w-2xl mx-auto' elevation={1}>
        <Typography variant='h6' className='mb-4'>Group details</Typography>

        <TextField
          variant='outlined'
          fullWidth
          label='Group Name'
          style={{ marginBottom: '20px' }}
          value={name}
          onChange={e => setName(e.target.value)}
        />

        <TextField
          variant='outlined'
          fullWidth
          label='Join Code'
          style={{ marginBottom: '20px' }}
          value={joinCode}
          error={!!joinCode && joinCode.trim().length < 5}
          helperText='Join code must be at least 5 characters.'
          onChange={e => setJoinCode(e.target.value)}
        />

        <Typography variant='h6' className='mt-4 mb-2'>Group admin</Typography>
        {selectedAdmin
          ? (
            <div className='flex items-center justify-between mb-5 p-3 border rounded'>
              <span>
                <strong>{selectedAdmin.name || 'Unnamed user'}</strong>
                <span className='text-gray-500 ml-2'>{selectedAdmin.email}</span>
              </span>
              <Button size='small' onClick={clearAdmin}>Change</Button>
            </div>
            )
          : (
            <div className='mb-5'>
              <TextField
                variant='outlined'
                fullWidth
                label='Search admin by email'
                value={adminEmail}
                onChange={e => setAdminEmail(e.target.value)}
                InputProps={{ endAdornment: searching ? <CircularProgress size={18} /> : null }}
              />
              {results.length > 0 &&
                <Paper variant='outlined' className='mt-1 max-h-60 overflow-auto'>
                  {results.map(user => (
                    <MenuItem key={user.id} onClick={() => { setSelectedAdmin(user); setResults([]) }}>
                      <span>
                        <strong>{user.name || 'Unnamed user'}</strong>
                        <span className='text-gray-500 ml-2'>{user.email}</span>
                      </span>
                    </MenuItem>
                  ))}
                </Paper>}
              {!searching && adminEmail.length >= 3 && results.length === 0 &&
                <Typography variant='body2' className='text-gray-500 mt-1'>No users found.</Typography>}
            </div>
            )}

        <Typography variant='h6' className='mt-4 mb-2'>Admin permissions</Typography>
        <Typography variant='body2' className='text-gray-500 mb-3'>
          Choose the access level for each area. Higher levels include the ones below.
        </Typography>
        {PERMISSION_DOMAINS.map(domain => (
          <div key={domain.key} className='flex items-center justify-between mb-3'>
            <span>{domain.label}</span>
            <Select
              size='small'
              style={{ minWidth: 160 }}
              value={levels[domain.key]}
              displayEmpty
              onChange={e => setLevel(domain.key, e.target.value)}
            >
              <MenuItem value=''>No access</MenuItem>
              {PERMISSION_LEVELS.map(level => (
                <MenuItem key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </div>
        ))}

        <div className='flex justify-end gap-3 mt-6'>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant='contained' disabled={!isValid || submitting} onClick={submit}>
            {submitting ? 'Creating…' : 'Create Group'}
          </Button>
        </div>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default GroupCreate
